import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import "./GeneratePage.scss";
import { message } from "antd";
import dayjsTZ from "../../../utils/dayjs";
import GeneratePageHeader from "./GeneratePageHeader";
import ReportContent from "./ReportContent/ReportContent";
import { useSelector } from "react-redux";
import { processFactory } from "../processFactory/processFactory";
import LoadableComp from "./../../commonComponents/LoadableComp/LoadableComp";
import { API } from "aws-amplify";
import { useRedux } from "../../../hooks/useRedux";
import { useReportsData } from "./reportContext/reportHelpers";
import { useReportTemplates } from "./reportTemplates";

const REPORT_TYPES_LIST = [
  "approvals",
  "supervisorApprovals",
  "clockingTypesReports",
  "requests",
  "bankForwardPay",
  "prepayments",
  "detailedTaxes",
  "employeesList",
  "suspensions",
  "employeesLeave",
];

function GeneratePage({}) {
  const { reportType } = useParams();
  const navigate = useNavigate();
  const [reportClockings, setReportClockings] = useRedux(
    "reportClockings",
    [],
    false,
  );
  const { changeDrawerFilter } = useReportsData();
  // shared report configuration, overridden by the user's active template
  const { constants } = useReportTemplates();

  const [
    { wages },
    { clockings },
    { isDarkMode },
    { prepayments },
    { employees },
    { employeeRequests },
  ] = useSelector((state) => [
    state.wages,
    state.clockings,
    state.usersList,
    state.prepayments,
    state.employeesList,
    state.employeeRequests,
  ]);

  const [loading, setLoading] = useState(true);
  const [updatingTable, setUpdatingTable] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const [customFilter, setCustomFilter] = useState(null);
  const [manualColumns, setManualColumns] = useState(null);

  // single dispatch: only the active report type is processed
  // memo recomputes purely on referential dependency changes
  const contentData = useMemo(() => {
    if (loading || !REPORT_TYPES_LIST.includes(reportType)) return undefined;
    const def = constants[reportType];

    switch (reportType) {
      case "approvals":
      case "clockingTypesReports":
        // source rows are read from the redux store inside the processor
        return processFactory(
          reportType,
          [],
          activeFilter,
          def,
          manualColumns,
          employees,
        );
      case "supervisorApprovals":
        return processFactory(reportType, [], activeFilter, def, manualColumns);
      case "requests":
        return processFactory(
          reportType,
          (employeeRequests || []).map((el) => ({
            ...el,
            employeeDetails: employees?.find((e) => e.userSub === el.userSub),
          })),
          activeFilter,
          def,
          manualColumns,
        );
      case "bankForwardPay":
      case "detailedTaxes":
        return processFactory(
          reportType,
          (wages || []).map((el) => ({
            ...el,
            employeeDetails: employees?.find(
              (e) => e.employeeId === el.employeeId,
            ),
          })),
          activeFilter,
          def,
          manualColumns,
        );
      case "prepayments":
        return processFactory(
          reportType,
          (prepayments || []).map((el) => ({
            ...el,
            employeeDetails:
              employees?.find((e) => e.employeeId === el.employeeId) || {},
          })),
          activeFilter,
          def,
          manualColumns,
        );
      case "employeesList":
      case "suspensions":
        return processFactory(
          reportType,
          employees,
          activeFilter,
          def,
          manualColumns,
        );
      case "employeesLeave": {
        const array = (clockings || []).map((el) => {
          const employee =
            (employees || []).find((e) => e.employeeId === el.employeeId) || {};
          return { ...el, employeeDetails: { ...employee } };
        });
        return processFactory(
          reportType,
          array,
          activeFilter,
          def,
          manualColumns,
        );
      }
      default:
        return undefined;
    }
  }, [
    reportType,
    loading,
    constants,
    activeFilter,
    manualColumns,
    employees,
    clockings,
    wages,
    prepayments,
    employeeRequests,
    reportClockings,
  ]);

  // region RETRIEVE FILTERS
  const filterReportsClockings = async (e) => {
    if (JSON.stringify(e) !== JSON.stringify(activeFilter)) {
      !!customFilter && setCustomFilter(null);
      setUpdatingTable(true);
      await getReportsClockings(e?.timeInterval, e?.employeeId)
        .then((r) => {
          const newClockings = [];
          r.forEach((cl) => {
            const idx = (employees || []).findIndex(
              (el) => el?.employeeId == cl?.employeeId,
            );
            if (idx !== -1) {
              const employeeDetails = employees[idx];
              newClockings.push({ ...cl, employeeDetails });
            } else {
              newClockings.push(cl);
            }
          });

          setActiveFilter(e);
          if (!!e?.employeeFullName) {
            changeDrawerFilter((prev = {}) => ({
              ...prev,
              Punonjësi: [e?.employeeFullName],
            }));
          }
          setReportClockings(
            newClockings.sort((a, b) => b?.clockOutDate - a?.clockOutDate),
          );
          message.success({
            key: "approvals",
            content: "Fetched successfully!",
          });
          setUpdatingTable(false);
        })
        .catch((err) => {
          console.log("Error: ", err);
          message.error({
            key: "approvals",
            content: "Failed fetching clockings!",
          });
          setUpdatingTable(false);
        });
    }
  };
  const updateColumns = (cols, callback) => {
    !!callback && callback();
    setManualColumns(cols);
  };
  const onGenerate = (filter) => {
    const source = contentData || { rowData: [], columnsDef: [] };
    const activeFields = Object.entries(filter || {}).filter(
      ([, values]) => Array.isArray(values) && values.length,
    );

    if (!activeFields.length) {
      setCustomFilter(null);
      return;
    }

    const columnsDef = source.columnsDef || [];
    // map each filtered field (a column label) to its column index, so we match
    // the selected values against the right column only (AND across fields).
    const fieldIndexes = activeFields
      .map(([label, values]) => [columnsDef.indexOf(label), values])
      .filter(([idx]) => idx !== -1);

    const arr = {
      ...source,
      rowData: (source.rowData || []).filter((row) =>
        fieldIndexes.every(([idx, values]) => values.includes(row[idx])),
      ),
    };
    setCustomFilter(arr);
  };

  useLayoutEffect(() => {
    if (
      !!clockings &&
      !!employees &&
      !!employeeRequests &&
      !!wages &&
      !!prepayments
    )
      setLoading(false);
  }, [clockings, employees, employeeRequests, wages, prepayments]);

  useEffect(() => {
    if (!!reportType && !REPORT_TYPES_LIST.includes(reportType)) {
      setTimeout(() => {
        navigate("/raportet");
      }, 1500);
    }
  }, [reportType]);

  //region RETURN
  return (
    <LoadableComp loading={loading}>
      <div className="globalPageWrapper">
        <div className={`generate-page ${isDarkMode ? "dark" : ""}`}>
          <GeneratePageHeader
            {...{
              filterReportsClockings,
              contentData,
              updateColumns,
            }}
            src={reportType}
          />
          <ReportContent
            page={reportType}
            onGenerate={onGenerate}
            infos={constants}
            activeFilter={activeFilter}
            updatingTable={updatingTable}
            resetCustom={() => setCustomFilter(null)}
            unfilteredContentData={contentData}
            contentData={!!customFilter ? customFilter : contentData}
          />
        </div>
      </div>
    </LoadableComp>
  );
}

export default GeneratePage;

// reduce a picker value to the calendar day the user actually sees, then re-anchor
// it through dayjsTZ so the Albania-local day boundary uses the correct DST offset
// for THAT date. Passing the raw dayjs instant to dayjsTZ instead would re-convert
// it under a different season's offset and shift the day (e.g. 1 March -> 29 Feb).
const toLocalDayString = (value) =>
  value && typeof value.format === "function"
    ? value.format("YYYY-MM-DD")
    : dayjsTZ(value).format("YYYY-MM-DD");

export const getReportsClockings = async (
  timeInterval,
  employeeId = false,
  dayType = false,
) => {
  return new Promise(async (resolve, reject) => {
    let filterClockingObj = {
      limiter: dayjsTZ(toLocalDayString(timeInterval[0]))
        .startOf("day")
        .valueOf(),
      endLimiter: dayjsTZ(toLocalDayString(timeInterval[1]))
        .add(14, "days")
        .endOf("day")
        .valueOf(),
    };
    if (employeeId) {
      filterClockingObj.employeeId = employeeId;
    }
    if (dayType) {
      filterClockingObj.dayType = dayType;
    }
    await API.get(`clocking`, `/clocking`, {
      queryStringParameters: {
        ...filterClockingObj,
        // limiter: moment(timeInterval[0]).startOf("day").valueOf(),
        // endLimiter: moment(timeInterval[1])
        //   .add(14, "days")
        //   .endOf("day")
        //   .valueOf(),
        // limiter: moment("01/1/2022", "DD/MM/YYYY").valueOf(),
        // start: params,
        // end: moment().valueOf(),
      },
    })
      .then((r) => resolve(r))
      .catch((error) => {
        console.error(error);
        reject(error);
      });
  });
};
